import { useState, useEffect, useCallback, useRef } from "react";

import { toast } from "sonner";

import {
  ArrowLeft,
  ArrowRight,
  Loader2,
  CheckCircle2,
  PartyPopper,
} from "lucide-react";

import { Button } from "@/components/common/Button";

import { StepIndicator } from "@/components/application-form/StepIndicator";

import { AccountStep } from "@/components/application-form/steps/AccountStep";
import { VerifyEmailStep } from "@/components/application-form/steps/VerifyEmailStep";
import { PersonalStep } from "@/components/application-form/steps/PersonalStep";
import { AcademicStep } from "@/components/application-form/steps/AcademicStep";
import { AddressStep } from "@/components/application-form/steps/AddressStep";
import { PreferencesStep } from "@/components/application-form/steps/PreferencesStep";
import { DeclarationsStep } from "@/components/application-form/steps/DeclarationsStep";
import { SubmitStep } from "@/components/application-form/steps/SubmitStep";
import { generateApplicationPDF } from "@/utils/applicationPdf";

import { useAuth } from "@/context/AuthContext";

import { applicationService } from "@/services/applicationService";
import { paymentService } from "@/services/paymentService";
import { authService } from "@/services/authService";

import { formatApiError } from "@/services/api";

import { useExamFee } from "@/context/ExamFeeContext";

import SEO from "@/components/SEO";

/**
 * 4 steps:
 * 1. Account      (create account + verify email)
 * 2. Personal     (personal + academic + address)
 * 3. Preferences  (preferences + declarations)
 * 4. Submit       (review on top, payment below)
 */
const TOTAL = 4;

export default function ApplyForExamPage() {
  const { user, register } = useAuth();
  const { examFee, loading: examFeeLoading } = useExamFee();

  const [step, setStep] = useState(1);

  /**
   * Step 1 has two stages:
   * "form" -> account creation form
   * "otp"  -> email OTP verification
   */
  const [accountStage, setAccountStage] = useState("form");

  const [busy, setBusy] = useState(false);
  const [resendBusy, setResendBusy] = useState(false);
  const [application, setApplication] = useState(null);
  const [done, setDone] = useState(false);

  /*
   * Remembers that the email was verified in this session,
   * even if AuthContext user object is still stale.
   */
  const verifiedRef = useRef(false);

  /**
   * Account
   */
  const [account, setAccount] = useState({
    fullName: "",
    email: "",
    phone: "",
    password: "",
  });

  /**
   * Application sections
   */
  const [personal, setPersonal] = useState({});
  const [academic, setAcademic] = useState({});
  const [address, setAddress] = useState({});
  const [preferences, setPreferences] = useState({});
  const [declarations, setDeclarations] = useState({});

  /**
   * Scroll to top whenever the step (or account stage) changes
   */
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, [step, accountStage]);

  /**
   * Hydrate application
   */
  const hydrate = useCallback((app) => {
    setApplication(app);
    setPersonal(app.personal || {});
    setAcademic(app.academic || {});
    setAddress(app.address || {});
    setPreferences(app.preferences || {});
    setDeclarations(app.declarations || {});
  }, []);

  /**
   * Load existing candidate application
   */
  useEffect(() => {
    let active = true;

    async function load() {
      if (!user || user.role !== "candidate") {
        return;
      }

      try {
        const app = await applicationService.getMine();

        if (!active) {
          return;
        }

        hydrate(app);

        /* Already submitted */
        if (app.status === "submitted") {
          setStep(TOTAL);
          return;
        }

        /* Account created but email not verified -> stay on step 1 (OTP stage) */
        const isVerified = user.emailVerified || verifiedRef.current;

        if (!isVerified) {
          setStep(1);
          setAccountStage("otp");
          return;
        }

        /* Verified candidate -> resume from saved step (min 2, max 4) */
        setStep(Math.min(Math.max(app.currentStep || 2, 2), TOTAL));
      } catch (error) {
        console.log("Application load skipped:", error?.message);
      }
    }

    load();

    return () => {
      active = false;
    };
  }, [user, hydrate]);

  /**
   * Generic field setter
   */
  const setField = (setter) => (key, value) => {
    setter((state) => ({
      ...state,
      [key]: value,
    }));
  };

  /**
   * Save application draft
   */
  async function saveDraft(extra = {}) {
    const payload = {
      personal,
      academic,
      address,
      preferences,
      declarations,
      ...extra,
    };

    const app = await applicationService.update(payload);

    setApplication(app);

    return app;
  }

  /**
   * Step 1 (form stage) — Account creation
   */
  // async function handleAccount() {
  //   if (
  //     !account.fullName ||
  //     !account.email ||
  //     !account.phone ||
  //     !account.password
  //   ) {
  //     toast.error("Please fill all fields to create your account.");

  //     return;
  //   }

  //   setBusy(true);

  //   try {
  //     await register(account);

  //     try {
  //       const app = await applicationService.getMine();

  //       hydrate(app);
  //     } catch {
  //       /* Application can still load after auth state updates. */
  //     }

  //     setPersonal((previous) => ({
  //       ...previous,
  //       fullName: account.fullName,
  //     }));

  //     toast.success(`Verification OTP sent to ${account.email}`);

  //     /* Stay on step 1, just show the OTP screen */
  //     setAccountStage("otp");
  //   } catch (e) {
  //     toast.error(formatApiError(e));
  //   } finally {
  //     setBusy(false);
  //   }
  // }


  async function handleAccount() {
  if (
    !account.fullName ||
    !account.email ||
    !account.phone ||
    !account.password
  ) {
    toast.error("Please fill all fields to create your account.");

    return;
  }

  setBusy(true);

  try {
    const registrationResult = await register(account);

    // ==========================================
    // FIRSTPROMOTER REFERRAL TRACKING
    // ==========================================
    try {
      const registeredUserId =
        registrationResult?.user?._id ||
        registrationResult?.user?.id ||
        registrationResult?.data?.user?._id ||
        registrationResult?.data?.user?.id ||
        registrationResult?._id ||
        registrationResult?.id;

      if (typeof window.fpr === "function") {
        const referralData = {
          email: account.email,
        };

        if (registeredUserId) {
          referralData.uid = String(registeredUserId);
        }

        window.fpr("referral", referralData);

        console.log(
          "FirstPromoter referral tracked:",
          referralData
        );
      } else {
        console.warn(
          "FirstPromoter fpr function is not available."
        );
      }
    } catch (fpError) {
      console.error(
        "FirstPromoter referral tracking failed:",
        fpError
      );
    }
    // ==========================================
    // END FIRSTPROMOTER
    // ==========================================

    try {
      const app = await applicationService.getMine();

      hydrate(app);
    } catch {
      /* Application can still load after auth state updates. */
    }

    setPersonal((previous) => ({
      ...previous,
      fullName: account.fullName,
    }));

    toast.success(`Verification OTP sent to ${account.email}`);

    setAccountStage("otp");
  } catch (e) {
    toast.error(formatApiError(e));
  } finally {
    setBusy(false);
  }
}

  /**
   * Step 1 (otp stage) — Verify email
   */
  async function handleVerifyEmail(otp) {
    setBusy(true);

    try {
      const result = await authService.verifyEmail(otp);

      verifiedRef.current = true;

      try {
        const app = await applicationService.getMine();

        hydrate(app);
      } catch {
        /* Continue from local state. */
      }

      toast.success("Email verified successfully!");

      /* Step 1 complete -> go to Personal */
      setStep(2);

      return result;
    } catch (e) {
      /* Throw so VerifyEmailStep can display it. */
      throw new Error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  }

  /**
   * Resend OTP
   */
  async function handleResendOTP() {
    setResendBusy(true);

    try {
      const result = await authService.resendEmailOTP();

      toast.success("A new verification OTP has been sent to your email.");

      return result;
    } catch (e) {
      throw new Error(formatApiError(e));
    } finally {
      setResendBusy(false);
    }
  }

  /**
   * Continue (steps 2 and 3)
   */
  async function next() {
    if (step === 1) {
      return handleAccount();
    }

    /* Step 4 uses Pay / Submit buttons */
    if (step >= TOTAL) {
      return;
    }

    /* Declarations are part of step 3 */
    if (
      step === 3 &&
      (!declarations.infoAccurate || !declarations.termsAccepted)
    ) {
      toast.error("Please accept both declarations to continue.");

      return;
    }

    setBusy(true);

    try {
      await saveDraft({
        currentStep: step + 1,
      });

      setStep((currentStep) => Math.min(currentStep + 1, TOTAL));
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  }

  /**
   * Razorpay Payment (step 4)
   */
  async function handlePay() {
    if (!window.Razorpay) {
      toast.error("Razorpay checkout failed to load. Please refresh the page.");

      return;
    }

    setBusy(true);

    try {
      const order = await paymentService.createOrder();

      if (!order?.orderId) {
        throw new Error("Unable to create Razorpay order.");
      }

      if (!order?.keyId) {
        throw new Error("Razorpay key is not available.");
      }

      const options = {
        key: order.keyId,

        amount: order.amount,

        currency: order.currency || "INR",

        name: "CET — Corporate Entrance Test",

        description: "CET 2027 Application Fee",

        order_id: order.orderId,

        prefill: {
          name: account.fullName || user?.name || "",
          email: account.email || user?.email || "",
          contact: account.phone || user?.phone || "",
        },

        notes: {
          applicationId: application?._id || application?.id || "",
        },

        theme: {
          color: "#F5A623",
        },

        handler: async function (response) {
          try {
            setBusy(true);

            const app = await paymentService.verify({
              razorpay_order_id: response.razorpay_order_id,
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_signature: response.razorpay_signature,
            });

            setApplication(app);

            toast.success(
              "Payment successful! Now click Submit Application to finish."
            );

            /* Stay on step 4 — Submit button appears now */
          } catch (error) {
            toast.error(formatApiError(error));
          } finally {
            setBusy(false);
          }
        },

        modal: {
          ondismiss: function () {
            setBusy(false);

            toast.info("Payment window closed.");
          },
        },
      };

      const razorpay = new window.Razorpay(options);

      razorpay.on("payment.failed", function (response) {
        setBusy(false);

        const message =
          response?.error?.description || "Payment failed. Please try again.";

        toast.error(message);
      });

      razorpay.open();
    } catch (e) {
      setBusy(false);

      toast.error(formatApiError(e));
    }
  }

  /**
   * Submit application (step 4)
   */
  async function handleSubmit() {
    setBusy(true);

    try {
      const app = await applicationService.submit();

      setApplication(app);

      try {
        await generateApplicationPDF(app);

        toast.success(
          "Application submitted successfully! Your application PDF has been downloaded."
        );
      } catch (pdfError) {
        console.error("Application PDF generation failed:", pdfError);

        /* Application is already submitted — PDF failure is not a submission failure. */
        toast.warning(
          "Application submitted successfully, but PDF download failed. Please contact support."
        );
      }

      setDone(true);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  }

  /**
   * Success screen
   */
  if (done || application?.status === "submitted") {
    return (
      <>
        <SEO page="apply" />

        <div className="min-h-screen bg-slate-50 flex items-center justify-center p-6">
          <div
            className="max-w-lg w-full rounded-2xl bg-white border border-slate-200 shadow-sm p-8 text-center"
            data-testid="apply-success"
          >
            <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-green-100">
              <PartyPopper className="h-8 w-8 text-green-600" />
            </div>

            <h1 className="mt-6 font-heading text-2xl font-bold text-slate-900">
              Application Submitted!
            </h1>

            <p className="mt-2 text-slate-600">
              Your CET 2027 application has been received. Save your
              application number for reference.
            </p>

            <div className="mt-5 rounded-lg bg-slate-900 text-white py-4">
              <div className="text-xs uppercase tracking-widest text-slate-400">
                Application Number
              </div>

              <div className="font-heading text-xl font-extrabold text-[#F5A623]">
                {application?.applicationNumber}
              </div>
            </div>

            <div className="mt-6 flex gap-3 justify-center">
              <Button to="/dashboard" data-testid="success-dashboard-btn">
                Go to Dashboard
              </Button>

              <Button to="/" variant="outline">
                Home
              </Button>
            </div>
          </div>
        </div>
      </>
    );
  }

  const paid = application?.payment?.status === "paid";

  /**
   * Main UI
   */
  return (
    <>
      <SEO page="apply" />

      <div className="min-h-screen bg-slate-50 py-10">
        <div className="max-w-3xl mx-auto px-4 sm:px-6">
          {/* Header */}

          <div className="mb-6">
            <h1 className="font-heading text-2xl sm:text-3xl font-bold text-slate-900">
              CET 2027 — Application Form
            </h1>

            <p className="text-sm text-slate-500 mt-1">
              Applications open October 1, 2026 and close December 31, 2026.
              Your progress is saved automatically at every step.
            </p>
          </div>

          <div className="rounded-2xl bg-white border border-slate-200 shadow-sm p-6 sm:p-8">
            {/* Step Indicator */}

            <StepIndicator current={step} />

            {/* Step Content */}

            <div className="mt-8">
              {/* Step 1 — Account (create account -> verify email) */}

              {step === 1 && accountStage === "form" && (
                <AccountStep
                  values={account}
                  onChange={setField(setAccount)}
                />
              )}

              {step === 1 && accountStage === "otp" && (
                <VerifyEmailStep
                  email={account.email || user?.email}
                  onVerify={handleVerifyEmail}
                  onResend={handleResendOTP}
                  busy={busy}
                  resendBusy={resendBusy}
                />
              )}

              {/* Step 2 — Personal (personal + academic + address) */}

              {step === 2 && (
                <div className="space-y-10">
                  <PersonalStep
                    values={personal}
                    onChange={setField(setPersonal)}
                  />

                  <div className="border-t border-slate-100" />

                  <AcademicStep
                    values={academic}
                    onChange={setField(setAcademic)}
                  />

                  <div className="border-t border-slate-100" />

                  <AddressStep
                    values={address}
                    onChange={setField(setAddress)}
                  />
                </div>
              )}

              {/* Step 3 — Preferences (preferences + declarations) */}

              {step === 3 && (
                <div className="space-y-10">
                  <PreferencesStep
                    values={preferences}
                    onChange={setField(setPreferences)}
                  />

                  <div className="border-t border-slate-100" />

                  <DeclarationsStep
                    values={declarations}
                    onChange={setField(setDeclarations)}
                  />
                </div>
              )}

              {/* Step 4 — Submit (review on top, payment below) */}

              {step === 4 && (
                <SubmitStep application={application} paid={paid} />
              )}
            </div>

            {/* Navigation */}

            <div className="mt-8 flex items-center justify-between gap-3 border-t border-slate-100 pt-6">
              {/* Back (disabled on step 1 and 2 — account is already created/verified) */}

              <Button
                variant="outline"
                onClick={() =>
                  setStep((currentStep) => Math.max(currentStep - 1, 1))
                }
                disabled={step <= 2 || busy}
                data-testid="apply-back-btn"
              >
                <ArrowLeft className="h-4 w-4" />
                Back
              </Button>

              {/* Step 1 — Create Account (OTP stage has its own Verify button) */}

              {step === 1 && accountStage === "form" && (
                <Button
                  onClick={next}
                  disabled={busy}
                  data-testid="apply-next-btn"
                >
                  {busy ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <>
                      Create Account
                      <ArrowRight className="h-4 w-4" />
                    </>
                  )}
                </Button>
              )}

              {/* Steps 2-3 — Continue */}

              {(step === 2 || step === 3) && (
                <Button
                  onClick={next}
                  disabled={busy}
                  data-testid="apply-next-btn"
                >
                  {busy ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <>
                      Continue
                      <ArrowRight className="h-4 w-4" />
                    </>
                  )}
                </Button>
              )}

              {/* Step 4 — Pay first */}

              {step === 4 && !paid && (
  <Button
    onClick={handlePay}
    disabled={busy || examFeeLoading || examFee === null}
    data-testid="apply-pay-btn"
  >
    {busy || examFeeLoading ? (
      <Loader2 className="h-4 w-4 animate-spin" />
    ) : (
      <>
        Pay ₹{examFee} &amp; Continue
        <ArrowRight className="h-4 w-4" />
      </>
    )}
  </Button>
)}

              {/* Step 4 — Submit after payment */}

              {step === 4 && paid && (
                <Button
                  onClick={handleSubmit}
                  disabled={busy}
                  data-testid="apply-submit-btn"
                >
                  {busy ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <>
                      Submit Application
                      <CheckCircle2 className="h-4 w-4" />
                    </>
                  )}
                </Button>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
}